import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { withStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import Typography from '@material-ui/core/Typography';

import Chip from 'components/common/Chip';
import Button from 'components/common/Button';
import Tooltip from 'components/common/Tooltip';

import * as fromFieldActions from 'redux/modules/formControl';

import pxToRem from 'styles/pxToRem';
import pxToEm from 'styles/pxToEm';
import hexToRgb from 'styles/hexToRgb';

const styles = theme => ({
  textarea: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    position: 'relative',
    padding: 10,
    border: `1px solid ${theme.palette.grey[600]}`,
    borderRadius: 4,
    backgroundColor: theme.palette.common.white,
  },
  textareaActive: {
    borderColor: theme.palette.primary[500],
    backgroundColor: `rgba(${hexToRgb(theme.palette.primary[500])}, 0.1)`,
    '& $inputInput': {
      caretColor: theme.palette.primary[500],
    },
  },
  textareaError: {
    borderColor: theme.palette.validation[500],
  },
  chip: {
    margin: 5,
    verticalAlign: 'top',
  },
  chipGroup: {
    display: 'inline-flex',
    margin: 5,
    verticalAlign: 'top',
    '& $chip': {
      margin: 0,
    },
    '& $chipSeparator': {
      marginTop: 0,
      marginBottom: 0,
    },
    '& $tooltip': {
      margin: 0,
    },
  },
  chipSeparator: {
    fontSize: pxToRem(13),
    display: 'inline-flex',
    margin: '5px 6px',
    lineHeight: '30px',
  },
  separatorButton: {
    position: 'absolute',
    right: 10,
    bottom: 10,
    zIndex: 1,
    boxShadow: 'none',
  },
  inputChip: {
    display: 'inline-flex',
    position: 'relative',
    margin: 6,
    verticalAlign: 'top',
  },
  inputValue: {
    fontSize: pxToRem(14),
    letterSpacing: pxToEm(0.5, 14),
    lineHeight: pxToEm(16, 14),
    height: pxToEm(16, 14),
    padding: '6px 8px 8px 0',
    fontFamily: theme.typography.fontFamily,
    opacity: 0,
  },
  inputRoot: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1,
  },
  inputInput: {
    fontSize: pxToRem(14),
    letterSpacing: pxToEm(0.5, 14),
    lineHeight: pxToEm(16, 14),
    height: pxToEm(16, 14),
    padding: '6px 0 8px',
    color: theme.palette.secondary[800],
    '&::placeholder': {
      opacity: 1,
      color: `rgba(${hexToRgb(theme.palette.text.primary)}, 0.54)`,
    },
  },
  tooltip: {
    display: 'inline-flex',
    margin: 5,
    verticalAlign: 'top',
    '& $chip': {
      margin: 0,
    },
  },
  label: {
    position: 'relative',
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 4,
    },
    fontSize: pxToRem(12),
    letterSpacing: pxToEm(0.5),
    fontWeight: 700,
    color: theme.palette.grey[600],
    transform: 'none',
  },
});

class HCPCCOdesTextareaField extends Component {
  constructor(props) {
    super(props);

    this.state = {
      inputValue: '',
      active: false,
      hovered: false,
      separator: false,
    };

    this.onInputChange = this.onInputChange.bind(this);
    this.onInputFocus = this.onInputFocus.bind(this);
    this.onInputBlur = this.onInputBlur.bind(this);
    this.onInputKeyDown = this.onInputKeyDown.bind(this);
    this.onChipFocus = this.onChipFocus.bind(this);
    this.onChipBlur = this.onChipBlur.bind(this);
    this.onChipDelete = this.onChipDelete.bind(this);
    this.onTextareaClick = this.onTextareaClick.bind(this);
    this.onTextareaMouseOver = this.onTextareaMouseOver.bind(this);
    this.onTextareaMouseOut = this.onTextareaMouseOut.bind(this);
    this.onSeparatorButtonClick = this.onSeparatorButtonClick.bind(this);
    this.onSeparatorButtonFocus = this.onSeparatorButtonFocus.bind(this);
    this.onSeparatorButtonBlur = this.onSeparatorButtonBlur.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onLabelClick = this.onLabelClick.bind(this);
  }

  onInputChange(event) {
    const { value } = event.target;
    const { input, meta } = this.props;
    if (value.toLowerCase() === 'or'
      && !this.state.separator
      && input.value.length !== 0) {
      this.setState({
        inputValue: '',
        separator: true,
      });
    } else {
      const prefix = value.slice(0, 1).toUpperCase().replace(/[^A-Z]/g, '');
      const code = value.slice(1).replace(/[^\d]/g, '');
      const inputValue = `${prefix}${code}`;
      if (/^[A-Z]+\d{4}$/i.test(inputValue)
        && !meta.asyncValidating) {
        this.onChange(inputValue);
      } else {
        this.setState({ inputValue });
      }
    }
  }

  onInputFocus() {
    this.setState({ active: true });
    this.props.actions.focusFormControl(this.props.input.name);
  }

  onInputBlur() {
    this.setState({ active: false });
    this.props.actions.blurFormControl();
  }

  onInputKeyDown(event) {
    const { value } = event.target;
    const { input, meta } = this.props;
    const { separator } = this.state;
    switch (event.key) {
      case 'Enter':
        if (value.length > 0
          && /^[A-Z]+\d{4}$/i.test(value)
          && !meta.asyncValidating) {
          this.onChange(value);
        }
        break;
      case 'Backspace':
      case 'Delete': {
        if (value.length === 0) {
          if (separator) {
            this.setState({ separator: false });
          } else {
            const newValue = input.value.map((valueItem, valueItemIndex) => {
              if (valueItemIndex !== input.value.length - 1) {
                return valueItem;
              }
              const newValueItem = valueItem.slice(0, valueItem.length - 1);
              if (valueItem.length > 1) {
                this.setState({ separator: true });
              }
              return newValueItem;
            }).filter(valueItem => valueItem.length > 0);
            input.onChange(newValue);
          }
        }
        break;
      }
      default:
    }
  }

  onChipFocus() {
    if (!this.props.disabled) {
      this.setState({ active: true });
    }
  }

  onChipBlur() {
    this.setState({ active: false });
  }

  onChipDelete(value, index, nestedIndex) {
    const { input } = this.props;
    const newValue = input.value.map((valueItem, valueItemIndex) => {
      if (valueItemIndex !== index) {
        return valueItem;
      }
      const newValueItem = [
        ...valueItem.slice(0, nestedIndex),
        ...valueItem.slice(nestedIndex + 1),
      ];
      if (valueItem.length > 1) {
        this.setState({ separator: true });
      }
      return newValueItem;
    }).filter(valueItem => valueItem.length > 0);

    if (newValue.length === 0) {
      this.setState({ separator: false });
    }

    input.onChange(newValue);
    this.inputRef.focus();
  }

  onTextareaClick(event) {
    if (event.target === this.textareaRef) {
      this.inputRef.focus();
    }
  }

  onTextareaMouseOver() {
    this.setState({ hovered: true });
  }

  onTextareaMouseOut() {
    this.setState({ hovered: false });
  }

  onSeparatorButtonClick() {
    this.setState({ separator: !this.state.separator });
    this.inputRef.focus();
  }

  onSeparatorButtonFocus() {
    this.setState({ active: true });
  }

  onSeparatorButtonBlur() {
    this.setState({ active: false });
  }

  onChange(value) {
    const { input } = this.props;
    const { separator } = this.state;
    let newValue;
    if (separator) {
      newValue = [
        ...input.value.slice(0, input.value.length - 1),
        [
          ...input.value[input.value.length - 1]
            .slice(0, input.value[input.value.length - 1].length),
          value,
        ],
      ];
    } else {
      newValue = [
        ...input.value.slice(0, input.value.length),
        [value],
      ];
    }

    input.onChange(newValue);
    this.setState({
      inputValue: '',
      separator: false,
    });
    setTimeout(() => {
      this.inputRef.focus();
    }, 100);
  }

  onLabelClick() {
    if (this.inputRef) {
      this.inputRef.focus();
    }
  }

  render() {
    const {
      classes,
      fullWidth,
      id,
      minHeight,
      alignItems,
      input,
      meta,
      formControl,
      disabled,
      label,
    } = this.props;
    const { inputValue, active, hovered, separator } = this.state;
    const renderSingleChip = (item, index) => (
      <Tooltip
        key={`${item[0]}-${index}`}
        className={classes.tooltip}
        content={
          (meta.error && (meta.error.codes.indexOf(item[0]) !== -1)) ? (
            <Typography variant="subheading" color="secondary">
              {meta.error.text}
            </Typography>
          ) : ''
        }
        {...(meta.error && (meta.error.codes.indexOf(item[0]) !== -1)) ? {
          disableFocusListener: false,
          disableHoverListener: false,
          disableTouchListener: false,
        } : {
          disableFocusListener: true,
          disableHoverListener: true,
          disableTouchListener: true,
        }}
        color="white"
        placement="right"
      >
        <Chip
          className={classes.chip}
          size="md"
          {...index === 0 ? {
            borderColor: 'primary',
            color: 'primary',
          } : {
            borderColor: 'default',
            color: 'default',
          }}
          {...meta.error && (meta.error.codes.indexOf(item[0]) !== -1) ? {
            borderColor: 'error',
            color: 'error',
          } : {}}
          label={item[0]}

          {...!disabled ? {
            onDelete: () => {
              this.onChipDelete(item[0], index, 0);
            } } : {}
          }
          onKeyDown={(event) => {
            if (!disabled && ['Enter', 'Delete', 'Backspace'].indexOf(event.key) !== -1) {
              this.onChipDelete(item[0], index, 0);
            }
          }}
          onFocus={this.onChipFocus}
          onBlur={this.onChipBlur}
          clickable={!disabled}
        />
      </Tooltip>
    );
    const renderGroupedChip = (item, index, nestedItem, nestedIndex) => (
      <Tooltip
        className={classes.tooltip}
        content={
          (meta.error && (meta.error.codes.indexOf(nestedItem) !== -1)) ? (
            <Typography variant="subheading" color="secondary">
              {meta.error.text}
            </Typography>
          ) : ''
        }
        {...(meta.error && (meta.error.codes.indexOf(nestedItem) !== -1)) ? {
          disableFocusListener: false,
          disableHoverListener: false,
          disableTouchListener: false,
        } : {
          disableFocusListener: true,
          disableHoverListener: true,
          disableTouchListener: true,
        }}
        color="white"
        placement="right"
      >
        <Chip
          className={classes.chip}
          size="md"
          {...index === 0 ? {
            borderColor: 'primary',
            color: 'primary',
          } : {
            borderColor: 'default',
            color: 'default',
          }}
          {...meta.error && (meta.error.codes.indexOf(item[0]) !== -1) ? {
            borderColor: 'error',
            color: 'error',
          } : {}}
          label={nestedItem}
          {...!disabled ? {
            onDelete: () => {
              this.onChipDelete(nestedItem, index, nestedIndex);
            } } : {}
          }
          onKeyDown={(event) => {
            if (!disabled && ['Enter', 'Delete', 'Backspace'].indexOf(event.key) !== -1) {
              this.onChipDelete(nestedItem, index, nestedIndex);
            }
          }}
          onFocus={this.onChipFocus}
          onBlur={this.onChipBlur}
          clickable={!disabled}
        />
      </Tooltip>
    );
    const errorVisible =
      Boolean(meta.touched)
      && Boolean(meta.error)
      && Boolean(!meta.error.codes)
      && (active || (hovered && !formControl));
    const placeholder = input.value.length > 0 ? 'Enter additional codes' : 'Enter main code';
    return (
      <FormControl
        fullWidth={fullWidth}
        error={meta.touched && Boolean(meta.error)}
      >
        {label.length > 0 &&
          <InputLabel
            className={classes.label}
            disableAnimation
            htmlFor={input.id}
            shrink={false}
            onClick={this.onLabelClick}
          >
            {label}
          </InputLabel>
        }
        <Tooltip
          content={
            (meta.error && !meta.error.codes) ? (
              <Typography variant="subheading" color="secondary">
                {meta.error.text}
              </Typography>
            ) : ''
          }
          open={errorVisible}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          color="white"
          placement="top"
        >
          <div
            className={classNames(classes.textarea, {
              [classes.textareaActive]: active,
              [classes.textareaError]: meta.touched && meta.error && !meta.error.codes,
            })}
            onClick={this.onTextareaClick}
            onMouseOver={this.onTextareaMouseOver}
            onMouseOut={this.onTextareaMouseOut}
            role="presentation"
            ref={(textareaRef) => { this.textareaRef = textareaRef; }}
            style={{ minHeight, alignItems }}
          >
            {input.value.length > 0 && input.value.map((item, index) => (
              (item.length === 1) ? (
                renderSingleChip(item, index)
              ) : (
                <div
                  className={classes.chipGroup}
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${item[0]}-${index}`}
                >
                  {item.map((nestedItem, nestedIndex) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Fragment key={`${nestedItem}-${nestedIndex}`}>
                      {nestedIndex !== 0 &&
                        <Typography
                          className={classes.chipSeparator}
                          color="secondary"
                        >
                          or
                        </Typography>
                      }
                      {renderGroupedChip(item, index, nestedItem, nestedIndex)}
                    </Fragment>
                  ))}
                </div>
              )
            ))}
            {separator &&
              <Typography
                className={classes.chipSeparator}
                color="secondary"
              >
                or
              </Typography>
            }
            <div className={classes.inputChip}>
              <div className={classes.inputValue}>
                {inputValue.length > 0 ? inputValue : placeholder}
              </div>
              <Input
                id={id}
                value={inputValue}
                disableUnderline
                placeholder={placeholder}
                onChange={this.onInputChange}
                onFocus={this.onInputFocus}
                onBlur={this.onInputBlur}
                onKeyDown={this.onInputKeyDown}
                classes={{
                  root: classes.inputRoot,
                  input: classes.inputInput,
                }}
                inputRef={(inputRef) => { this.inputRef = inputRef; }}
                inputProps={{
                  maxLength: 5,
                  autoComplete: 'off',
                }}
                disabled={disabled}
              />
            </div>
            <Button
              className={classes.separatorButton}
              variant="fab"
              mini
              color="primary"
              disabled={input.value.length === 0 || disabled}
              onClick={this.onSeparatorButtonClick}
              onFocus={this.onSeparatorButtonFocus}
              onBlur={this.onSeparatorButtonBlur}
            >
              or
            </Button>
          </div>
        </Tooltip>
      </FormControl>
    );
  }
}

HCPCCOdesTextareaField.propTypes = {
  id: PropTypes.string.isRequired,
  fullWidth: PropTypes.bool,
  minHeight: PropTypes.number,
  alignItems: PropTypes.oneOf(['flex-start', 'flex-end', 'center']),
  formControl: PropTypes.string,
  input: PropTypes.shape({
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    value: PropTypes.arrayOf(
      PropTypes.arrayOf(PropTypes.string),
    ).isRequired,
  }).isRequired,
  meta: PropTypes.shape({
    asyncValidating: PropTypes.bool.isRequired,
    error: PropTypes.shape({
      codes: PropTypes.arrayOf(PropTypes.string),
      text: PropTypes.string,
    }),
  }).isRequired,
  disabled: PropTypes.bool,
  actions: PropTypes.shape({
    focusFormControl: PropTypes.func.isRequired,
    blurFormControl: PropTypes.func.isRequired,
  }).isRequired,
  label: PropTypes.string,
  classes: PropTypes.shape({
    textarea: PropTypes.string.isRequired,
    textareaActive: PropTypes.string.isRequired,
    textareaError: PropTypes.string.isRequired,
    chip: PropTypes.string.isRequired,
    separatorButton: PropTypes.string.isRequired,
    inputRoot: PropTypes.string.isRequired,
    inputInput: PropTypes.string.isRequired,
    tooltip: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
};

HCPCCOdesTextareaField.defaultProps = {
  fullWidth: false,
  minHeight: 120,
  alignItems: 'flex-start',
  formControl: null,
  disabled: false,
  label: '',
};

function mapStateToProps(state) {
  return {
    formControl: state.formControl,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(fromFieldActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(HCPCCOdesTextareaField),
);



// WEBPACK FOOTER //
// ./src/components/fields/HCPCCOdesTextareaField.js